/**
 * WebAuthn (passkey) browser plumbing — hand-rolled so we carry no extra dependency.
 *
 * The server (Spring Security's relying-party operations) issues creation/request options as the
 * standard WebAuthn JSON, in which every binary field (challenge, user.id, credential ids) is a
 * base64url string. `navigator.credentials.create/get` want ArrayBuffers there, and hand back a
 * `PublicKeyCredential` whose binary fields are ArrayBuffers — which must go back to the server as
 * base64url JSON. These helpers do that decode-in / encode-out round trip and nothing else.
 */

/** True when this browser can do WebAuthn at all — call before offering a passkey option. */
export const isWebAuthnSupported = (): boolean =>
  typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined'

const bufferToBase64Url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i += 1) binary += String.fromCharCode(bytes[i])
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

const base64UrlToBuffer = (value: string): ArrayBuffer => {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((value.length + 3) % 4)
  const binary = window.atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

const asObject = (options: unknown): Record<string, any> =>
  typeof options === 'string' ? JSON.parse(options) : (options as Record<string, any>)

type DescriptorJson = { type: PublicKeyCredentialType; id: string; transports?: AuthenticatorTransport[] }

const toDescriptors = (list?: DescriptorJson[]): PublicKeyCredentialDescriptor[] | undefined =>
  list?.map((d) => ({ type: d.type, id: base64UrlToBuffer(d.id), transports: d.transports }))

/** Serialize the browser's PublicKeyCredential (create or get) to the WebAuthn JSON the server reads. */
const serializeCredential = (credential: PublicKeyCredential): Record<string, unknown> => {
  const response = credential.response as AuthenticatorAttestationResponse & AuthenticatorAssertionResponse
  const json: Record<string, unknown> = {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {} as Record<string, unknown>,
  }
  const body = json.response as Record<string, unknown>
  body.clientDataJSON = bufferToBase64Url(response.clientDataJSON)
  if (typeof response.attestationObject !== 'undefined') {
    // Registration (attestation) response.
    body.attestationObject = bufferToBase64Url(response.attestationObject)
    if (typeof response.getTransports === 'function') body.transports = response.getTransports()
  } else {
    // Assertion response.
    body.authenticatorData = bufferToBase64Url(response.authenticatorData)
    body.signature = bufferToBase64Url(response.signature)
    body.userHandle = response.userHandle ? bufferToBase64Url(response.userHandle) : null
  }
  return json
}

/**
 * Run a registration ceremony against server-issued creation options (raw JSON or object) and
 * return the attestation `PublicKeyCredential` serialized for `POST /2fa/webauthn/register`.
 */
export const startRegistration = async (optionsJson: unknown): Promise<Record<string, unknown>> => {
  if (!isWebAuthnSupported()) throw new Error('This browser does not support passkeys.')
  const options = asObject(optionsJson)
  const publicKey: PublicKeyCredentialCreationOptions = {
    ...(options as any),
    challenge: base64UrlToBuffer(options.challenge),
    user: { ...options.user, id: base64UrlToBuffer(options.user.id) },
    excludeCredentials: toDescriptors(options.excludeCredentials),
  }
  const credential = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null
  if (!credential) throw new Error('Passkey registration was cancelled.')
  return serializeCredential(credential)
}

/**
 * Run an assertion ceremony against server-issued request options (raw JSON or object) and return
 * the assertion `PublicKeyCredential` serialized for `POST /2fa/webauthn/verify`.
 */
export const startAssertion = async (optionsJson: unknown): Promise<Record<string, unknown>> => {
  if (!isWebAuthnSupported()) throw new Error('This browser does not support passkeys.')
  const options = asObject(optionsJson)
  const publicKey: PublicKeyCredentialRequestOptions = {
    ...(options as any),
    challenge: base64UrlToBuffer(options.challenge),
    allowCredentials: toDescriptors(options.allowCredentials),
  }
  const credential = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null
  if (!credential) throw new Error('Passkey sign-in was cancelled.')
  return serializeCredential(credential)
}
